<template>
    <application-layout>
        <v-main>
            <v-container>
                <h1>Manage Redirects</h1>

                <p>
                    Below is the list of redirects that exist for the public-facing website. When adding a redirect, do
                    not include a leading or trailing slash (/), the domain, or the scheme. The exception is when you
                    are trying to create a redirect to the home page.
                </p>

                <h3>Examples:</h3>
                <ul class="list-unstyled">
                    <li>Old URL: locations</li>
                    <li>New URL: storage-locations</li>
                    <li>Result: This will redirect all requests to 'https://storespace.com/locations' to 'https://storespace.com/storage-locations'</li>
                    <li>&nbsp;</li>
                    <li>Old URL: about/about-us</li>
                    <li>New URL: content/pages/about-us</li>
                    <li>Result: This will redirect all requests to 'https://storespace.com/about-us' to 'https://storespace.com/content/pages/about-us'</li>
                    <li>&nbsp;</li>
                    <li>Old URL: content/test-page</li>
                    <li>New URL: /</li>
                    <li>Result: This will redirect all requests to 'https://storespace.com/content/test-page' to 'https://storespace.com/'</li>
                </ul>

                <v-dialog
                    v-model="addRedirect"
                    persistent
                    max-width="800"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn
                            data-testid="redirect-add-new-redirect-btn"
                            class="redirect-add-new-redirect-btn mb-4 ml-0"
                            v-on="on"
                            color="primary"
                        >
                            <v-icon>add</v-icon>
                            Add New Redirect
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title class="headline">Add New Redirect</v-card-title>
                        <v-divider />
                        <v-form ref="createRedirectForm" @submit.prevent="submitRedirectForm">
                            <v-card-text>
                                <app-input
                                    data-testid="redirect-add-old-url-input"
                                    class="redirect-add-old-url-input"
                                    :type="'text'"
                                    label="Old URL"
                                    v-model.trim="redirectData.from"
                                    :rules="{ required: true }"
                                    :useValidation="true"
                                />
                                <app-input
                                    data-testid="redirect-add-new-url-input"
                                    class="redirect-add-new-url-input"
                                    :type="'text'"
                                    label="New URL"
                                    v-model.trim="redirectData.to"
                                    :rules="{ required: true }"
                                    :useValidation="true"
                                />
                            </v-card-text>
                            <v-card-actions>
                                <v-btn
                                  data-testid="redirect-cancel-redirect-btn"
                                  class="redirect-cancel-redirect-btn"
                                  color="error"
                                  @click="addRedirect = false"
                                >
                                  Cancel
                                </v-btn>
                                <v-spacer />
                                <v-btn
                                    data-testid="redirect-add-redirect-btn"
                                    class="redirect-add-redirect-btn"
                                    color="primary"
                                    type="submit"
                                    :loading="loading"
                                >
                                    Add Redirect
                                </v-btn>
                            </v-card-actions>
                        </v-form>
                    </v-card>
                </v-dialog>

                <v-dialog
                    v-model="editRedirectDialog"
                    persistent
                    max-width="800"
                >
                    <v-card>
                        <v-card-title class="headline">Edit Redirect</v-card-title>
                        <v-form ref="editRedirectForm" @submit.prevent="submitEditRedirectForm">
                            <v-card-text>
                                <app-input
                                    data-testid="redirect-edit-old-url-input"
                                    class="redirect-edit-old-url-input"
                                    :type="'text'"
                                    label="Old URL"
                                    v-model.trim="redirectData.from"
                                    :rules="{ required: true }"
                                    :useValidation="true"
                                />
                                <app-input
                                    data-testid="redirect-edit-new-url-input"
                                    class="redirect-edit-new-url-input"
                                    :type="'text'"
                                    label="New URL"
                                    v-model.trim="redirectData.to"
                                    :rules="{ required: true }"
                                    :useValidation="true"
                                />
                            </v-card-text>
                            <v-card-actions>
                                <v-btn
                                  data-testid="redirect-cancel-edit-btn"
                                  class="redirect-cancel-edit-btn"
                                  color="error"
                                  @click="editRedirectDialog = false"
                                >
                                  Cancel
                                </v-btn>
                                <v-spacer />
                                <v-btn
                                    data-testid="redirect-save-redirect-btn"
                                    class="redirect-save-redirect-btn"
                                    color="primary"
                                    type="submit"
                                    :loading="loading"
                                >
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </v-form>
                    </v-card>
                </v-dialog>

                <v-data-table
                    :footer-props="{
                        ...footerProps,
                        itemsPerPageOptions: pagination.rowsPerPageOptions,
                    }"
                    :headers="headers"
                    :items="redirects"
                    :loading="loading"
                    :options.sync="pagination"
                    :server-items-length="pagination.totalItems"
                >
                    <template v-slot:body="{ items }">
                        <transition-group
                            name="scroll-y-transition"
                            tag="tbody"
                            v-if="items.length"
                        >
                            <tr v-for="item in items" :key="item.id">
                                <td>{{ item.from }}</td>
                                <td>{{ item.to }}</td>
                                <td>
                                    <v-btn
                                        data-testid="redirect-edit-redirect-btn"
                                        class="redirect-edit-redirect-btn mr-3 ml-0"
                                        small
                                        color="primary"
                                        @click="editRedirect(item.id)"
                                        :disabled="loading"
                                        :loading="loading"
                                    >
                                        Edit
                                    </v-btn>
                                    <v-btn
                                        data-testid="redirect-delete-redirect-btn"
                                        class="redirect-delete-redirect-btn ml-0"
                                        color="error"
                                        small
                                        @click="openDelete(item)"
                                    >
                                        Delete
                                    </v-btn>
                                </td>
                            </tr>
                        </transition-group>
                        <tr v-else>
                            <td colspan="12" style="text-align: center">
                                No redirects found.
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-container>
        </v-main>

        <delete-modal
            v-model="modal"
            @delete="deleteRedirect"
            :itemType="'redirect'"
            :advisory="advisory"
        />
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { footerProps } from "@/helpers/footer-props";
import { genericAdvisory } from "@/helpers/warning-advisory-text";
import { redirectHeaders } from "@/helpers/redirect/redirect-data-table-headers";

export default {
  data() {
    return {
      advisory: genericAdvisory,
      addRedirect: false,
      footerProps: footerProps,
      headers: redirectHeaders,
      redirectData: {
        from: null,
        to: null,
        id: null
      },
      tableLoading: true,
      loading: false,
      editRedirectDialog: false,
      modal: false,
      selectedRedirect: ""
    };
  },

  computed: {
    redirects() {
      return this.$store.state.redirect.redirects;
    },
    pagination: {
      get() {
        return this.$store.state.redirect.pagination;
      },
      set(newValue) {
        this.$store.commit("setRedirectPagination", newValue);
      }
    }
  },

  watch: {
    pagination: {
      handler() {
        this.tableLoading = true;
        this.$store.dispatch("getAllRedirects")
          .then(resp => {
            this.tableLoading = false;
          });
      },
      deep: true
    }
  },

  components: {
    ApplicationLayout,
    DeleteModal
  },

  methods: {
    submitRedirectForm() {
      this.loading = true;

      this.$store.dispatch("createRedirect", this.redirectData)
        .then(resp => {
          this.redirectData = {
            from: null,
            to: null
          };

          this.addRedirect = false;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    submitEditRedirectForm() {
      this.loading = true;

      this.$store.dispatch("updateRedirect", this.redirectData)
        .then(resp => {
          this.redirectData = {
            from: null,
            to: null
          };

          this.editRedirectDialog = false;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    async editRedirect(redirectId) {
      this.loading = true;

      await this.$store.dispatch("getRedirect", redirectId)
        .then(resp => {
          this.redirectData = {
            id: resp.data.redirect.id,
            from: resp.data.redirect.from,
            to: resp.data.redirect.to
          };

          this.editRedirectDialog = true;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    openDelete(redirect) {
      this.modal = true;
      this.selectedRedirect = redirect;
    },

    async deleteRedirect() {
      await this.$store.dispatch("deleteRedirect", this.selectedRedirect.id);
      this.modal = false;
      this.selectedRedirect = "";
    }
  }
};
</script>

<style lang="scss" scoped>
    ul.list-unstyled {
        list-style: none;
        margin-bottom: 30px;
        padding-left: 0;
    }
</style>
